import { FLAGS } from '@gozoki/feature-flags'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Download } from 'lucide-react'
import { useState } from 'react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs, type AppTab } from '~/components/new/app-ui/app-tabs'
import { QueryState } from '~/components/new/app-ui/query-state'
import { Title } from '~/components/new/app-ui/title'
import { SandboxBackButton } from '~/components/routes/sandbox-previsions/sandbox-back-button'
import { SALES_CORRECTION_READ } from '~/components/routes/sandbox-previsions/sandbox-tools'
import { buttonVariants } from '~/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { TabsContent } from '~/components/ui/tabs'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { FORECAST_REFERENCE_QUERY_OPTIONS } from '~/lib/forecast-reference-queries'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { DateRangeChip } from '../previsions/-date-range-chip'
import { PrevisionsStoreSelector } from '../previsions-long-terme/-previsions-store-selector'
import { AffectedTable, AffectedTableSkeleton, type AffectedRow } from './-affected-table'
import { BacktestPanel } from './-backtest-panel'
import { CorrectionModal } from './-correction-modal'
import { SelectionSummary, SelectionSummarySkeleton } from './-selection-summary'
import { correctionSearchSchema, defaultCorrectionRange, exclusiveEnd } from './-search-schema'

const TABS: AppTab<'corrections' | 'backtest'>[] = [
  { value: 'corrections', label: 'Corrections' },
  { value: 'backtest', label: 'Backtest' },
]

export const Route = createFileRoute('/_authenticated/correction-ruptures/')({
  validateSearch: correctionSearchSchema,
  // Ressource propre depuis l'audit du 19/09 (TAX-M3) : un seul `read` ouvrait huit écrans de
  // prévision. L'écran est en lecture seule, l'édition n'a pas encore d'affordance.
  staticData: { permission: SALES_CORRECTION_READ },
  component: CorrectionRupturesPage,
})
withRoutePermissions(Route)

function CorrectionRupturesPage() {
  const enabled = useFeatureFlag(FLAGS.salesUnitsCorrections)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [opened, setOpened] = useState<AffectedRow | null>(null)

  const setSearch = (next: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }) })

  const range = {
    end: search.to ?? defaultCorrectionRange().end,
    start: search.from ?? defaultCorrectionRange().start,
  }
  const to = exclusiveEnd(range.end)
  const page = search.page ?? 1
  const backtestStoreId = search.backtestStoreId ?? search.storeIds[0] ?? null

  // Boutiques du périmètre des corrections, pas de celui des prévisions (décision du 24/10) : même
  // requête que le sélecteur, servie une fois.
  const { data: selectable } = useQuery(
    api.forecast.salesCorrectionStores.queryOptions(
      {},
      { ...FORECAST_REFERENCE_QUERY_OPTIONS, enabled },
    ),
  )
  const storeLabels = new Map(
    (selectable?.stores ?? []).map((store) => [store.storeId, store.label]),
  )
  const storeLabelOf = (id: number) => storeLabels.get(id) ?? `Boutique ${id}`

  const groupsQuery = useQuery(
    api.forecast.salesCorrectionCategoryDays.queryOptions(
      { query: { storeIds: search.storeIds, from: range.start, to, page } },
      {
        enabled: enabled && search.storeIds.length > 0 && search.tab === 'corrections',
        placeholderData: keepPreviousData,
      },
    ),
  )

  if (!enabled) {
    return (
      <>
        <AppHeader>
          <Title>Correction des ruptures</Title>
        </AppHeader>
        <AppContainer>
          <p className="text-muted-foreground">Fonctionnalité non activée.</p>
        </AppContainer>
      </>
    )
  }

  return (
    <>
      <AppHeader>
        <div className="flex items-center gap-3">
          <SandboxBackButton />
          <Title>Correction des ruptures</Title>
          <a
            href="/note-correction-ruptures.pdf"
            download
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            <Download className="size-4" />
            Note méthodologique
          </a>
        </div>
      </AppHeader>
      <AppContainer>
        <div className="flex flex-wrap items-center gap-3">
          <PrevisionsStoreSelector
            source="sales-correction"
            selectedIds={search.storeIds}
            onApply={(storeIds) => setSearch({ storeIds, page: undefined })}
            placeholder="Sélectionner des boutiques"
            title="Boutiques à corriger"
          />
          <DateRangeChip
            direction="past"
            range={range}
            onChange={({ start, end }) => setSearch({ from: start, to: end, page: undefined })}
          />
        </div>

        <AppTabs
          className="mt-4"
          tabs={TABS}
          value={search.tab}
          onTabChange={(tab) => setSearch({ tab })}
        >
          <TabsContent value="corrections">
            {/* Sans boutique, la requête est désactivée : `QueryState` resterait sur le squelette,
                et les données d'une sélection précédente ne seraient jamais remplacées. */}
            {search.storeIds.length === 0 ? (
              <p className="text-muted-foreground">Sélectionne au moins une boutique.</p>
            ) : (
              <QueryState
                query={groupsQuery}
                skeleton={
                  <>
                    <SelectionSummarySkeleton />
                    <AffectedTableSkeleton />
                  </>
                }
                errorFallback="Impossible de charger les corrections."
              >
                {(data) => (
                  // Pendant le chargement d'une autre page (ou d'une autre sélection), la
                  // précédente reste affichée, estompée.
                  <div
                    aria-busy={groupsQuery.isPlaceholderData}
                    className={groupsQuery.isPlaceholderData ? 'opacity-60' : undefined}
                  >
                    <SelectionSummary totals={data.totals} />
                    <AffectedTable
                      data={data}
                      page={page}
                      onPageChange={(next) => setSearch({ page: next })}
                      storeLabels={storeLabels}
                      onOpen={setOpened}
                    />
                  </div>
                )}
              </QueryState>
            )}
          </TabsContent>

          <TabsContent value="backtest">
            {search.storeIds.length === 0 && (
              <p className="text-muted-foreground">Sélectionne au moins une boutique.</p>
            )}
            {backtestStoreId !== null && (
              <>
                {search.storeIds.length > 1 && (
                  <div className="mb-4 flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Boutique</span>
                    <Select
                      value={String(backtestStoreId)}
                      onValueChange={(value) => setSearch({ backtestStoreId: Number(value) })}
                    >
                      <SelectTrigger className="w-64">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {search.storeIds.map((storeId) => (
                          <SelectItem key={storeId} value={String(storeId)}>
                            {storeLabelOf(storeId)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <BacktestPanel storeId={backtestStoreId} from={range.start} to={to} />
              </>
            )}
          </TabsContent>
        </AppTabs>

        {opened && (
          <CorrectionModal
            row={opened}
            storeLabel={storeLabelOf(opened.storeId)}
            onClose={() => setOpened(null)}
          />
        )}
      </AppContainer>
    </>
  )
}
